import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeploymentConfig } from '../Model/Deployment';
import { useDeploymentViewModel } from '../ViewModel/DeploymentViewModel';
import { StepOne } from './DeployStepOne';
import StepThree from './DeployStepThree';
import StepTwo from './DeployStepTwo';
import { useRepoViewModel } from '../ViewModel/RepoViewModel';
import { standaloneLoader } from '../Components/Loader/StandAlondeLoader';
import { useConnectionStatus } from '../Components/ConnectionStatusProvider';
// import AWSSetupModal from '../Components/AwsConnection';
import { useSiteViewModel } from '../ViewModel/SiteViewModel';

// ── Step Progress ─────────────────────────────────────────────────────────────

const StepProgress: React.FC<{ currentStep: number; steps: { label: string; number: number }[] }> = ({ currentStep, steps }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '32px' }}>
    {steps.map((step, i) => (
      <React.Fragment key={step.number}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
          <div style={{
            width: '30px', height: '30px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', fontWeight: 700, flexShrink: 0,
            background: currentStep > step.number ? '#22c55e' : currentStep === step.number ? '#6366f1' : '#1e1e27',
            color: currentStep >= step.number ? '#fff' : '#52525f',
            border: currentStep < step.number ? '2px solid #32323d' : 'none',
          }}>
            {currentStep > step.number ? (
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6.5l4 4 5-5"/></svg>
            ) : step.number}
          </div>
          <div style={{
            fontSize: '10.5px', fontWeight: 500, whiteSpace: 'nowrap' as const,
            color: currentStep === step.number ? '#818cf8' : currentStep > step.number ? '#22c55e' : '#52525f',
          }}>
            {step.label}
          </div>
        </div>
        {i < steps.length - 1 && (
          <div style={{
            flex: 1, height: '2px', margin: '14px 8px 0',
            background: currentStep > step.number
              ? '#22c55e'
              : currentStep === step.number
                ? 'linear-gradient(90deg,#22c55e,#32323d)'
                : '#32323d',
          }}/>
        )}
      </React.Fragment>
    ))}
  </div>
);

// ── DeploymentFlow ────────────────────────────────────────────────────────────

export const DeploymentFlow: React.FC = () => {
  const { step, setStep, config, updateConfig, deploy, triggerDeployment } = useDeploymentViewModel();
  const [deploymentStatus, setDeploymentStatus] = useState<'idle' | 'deploying' | 'success' | 'error'>('idle');
  const navigate = useNavigate();
  const { repos, fetchRepos } = useRepoViewModel();
  const { checkConnectionStatus, showAWSModal, setShowAWSModal } = useConnectionStatus();
  const { dashboardData } = useSiteViewModel();
  const [deploymentId, setDeploymentId] = useState<string>('');
  const [monitorDeployment, setMonitorDeployment] = useState<boolean>(false);
  

  const repositoryOptions_ = [
    { label: 'Manual', value: 'manual' },
    { label: 'Automatic', value: 'automatic' },
  ];

  const deploymentEnv_ = [
    { label: 'Development', value: 'development' },
    { label: 'Staging', value: 'staging' },
    { label: 'Production', value: 'production' },
  ];

  useEffect(() => { 
  checkConnectionStatus();
  dashboardData();
  }, []);

  
  const repositoryOptions = repos.map(repo => ({ label: repo, value: repo.toLowerCase() }));

  const steps = [
    { label: 'Details', number: 1 },
    { label: 'Configure', number: 2 },
    { label: 'Review', number: 3 },
  ];

  const handleDeploy = async () => {
    try {
      const res = await deploy(config);
      if (res && res.success && res.data) {
        const id = res.data;
        setDeploymentId(id);
        const statusRes = await triggerDeployment(id);
        if (statusRes?.success) {
          navigate(`/sites/${id}`);
        }
      }
      if (res?.error) {
        standaloneLoader.showAlert(res.error);
        standaloneLoader.hideLoader();
      }
    } catch (error) {
      console.error('Unexpected error during deployment:', error);
    } finally {
      standaloneLoader.hideLoader();
    }
  };

  useEffect(() => { fetchRepos(); }, [fetchRepos]);
  useEffect(() => { checkConnectionStatus(); }, []);

  return (
    <div style={{ background: '#09090e', minHeight: '100vh', fontFamily: "'Inter',sans-serif" }}>

      {/* Topbar */}
      <div style={{
        height: '50px', background: '#111116', borderBottom: '1px solid #26262f',
        display: 'flex', alignItems: 'center', padding: '0 20px',
        position: 'sticky' as const, top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#8e8ea8' }}>
          <span
            style={{ cursor: 'pointer', color: '#818cf8' }}
            onClick={() => navigate('/sites')}
          >
            Deployments
          </span>
          <span style={{ color: '#52525f' }}>›</span>
          <span style={{ color: '#eeeef5', fontWeight: 500 }}>New Project</span>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '24px', maxWidth: '760px', margin: '0 auto' }}>

        {/* Page header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '20px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>
            Deploy Application
          </h1>
          <p style={{ fontSize: '13px', color: '#8e8ea8' }}>
            Connect your repo and deploy to any of your cloud accounts
          </p>
        </div>

        {/* Step progress */}
        <StepProgress currentStep={step} steps={steps} />

        {/* Step content */}
        <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '10px', padding: '24px' }}>
          {step === 1 && (
            <StepOne
              config={config}
              onNext={() => setStep(2)}
              repositoryOptions={repositoryOptions}
              analyzeOptions={repositoryOptions_}
              deploymentEnv={deploymentEnv_}
              updateConfig={updateConfig}
            />
          )}
          {step === 2 && (
            <StepTwo
              config={config}
              updateConfig={updateConfig}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          )}
          {step === 3 && (
            <StepThree
              config={config}
              onBack={() => setStep(2)}
              onDeploy={handleDeploy}
              deploymentStatus={deploymentStatus}
              isDeploying={deploymentStatus === 'deploying'}
              deploymentId={deploymentId}
              monitorDeployment={monitorDeployment}
              setMonitorDeployment={setMonitorDeployment}
            />
          )}
        </div>

        {/* Error banner */}
        {deploymentStatus === 'error' && (
          <div style={{
            marginTop: '16px', padding: '12px 16px', borderRadius: '6px',
            background: 'rgba(244,63,94,.1)', border: '1px solid rgba(244,63,94,.25)',
            display: 'flex', alignItems: 'center', gap: '10px', color: '#f43f5e', fontSize: '13px',
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="8" cy="8" r="6.5"/><path d="M8 5v3M8 10v.5"/>
            </svg>
            Deployment failed. Please check your configuration and try again.
          </div>
        )}
      </div>

      {/* <AWSSetupModal
        open={showAWSModal}
        onClose={() => setShowAWSModal(false)}
        onSuccess={() => setShowAWSModal(false)}
        externalId={userData?.external_id || ''}
      /> */}
    </div>
  );
};

interface StepThreeProps {
  config: DeploymentConfig;
  onBack: () => void;
  onDeploy: () => Promise<void>;
  deploymentStatus: 'idle' | 'deploying' | 'success' | 'error';
  isDeploying: boolean;
}